import {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {elementHelpers} from '../../utils/helpers/element.helpers';
import {formHelpers} from '../../utils/helpers/form.helpers';

const CONFIRM_Z_INDEX = 1003;

// X, Esc and the mask can fire repeatedly; only one confirmation may be open at a time.
let isConfirmOpen = false;

/**
 * Wraps a panel's close handler with an exit confirmation (H1MVP-4008).
 *
 * Pass `form` + `initialValues` for antd Form panels (modified = current values differ from
 * the opening values), or `isDirty` for panels whose state is not an antd Form. When the panel is
 * not modified the close handler runs straight away. A close that follows a successful save must
 * not go through the returned handler.
 *
 * @param {Object} params
 * @param {Function} [params.onClose] - The real close handler, run immediately or once confirmed.
 * @param {Object} [params.form] - antd form instance.
 * @param {Object} [params.initialValues] - Values the form opened with.
 * @param {boolean} [params.isDirty] - Explicit modified flag; takes precedence over the form diff.
 * @param {boolean} [params.extraDirty] - Extra modified flag for state outside the form, OR-ed with the form diff.
 * @returns {Function} Handler to pass as the panel's `onClose` / `onCancel`.
 */
export const useConfirmClose = ({onClose, form, initialValues, isDirty, extraDirty = false} = {}) => {
  const [t] = useTranslation('main');

  return useCallback((...args) => {
    if (isConfirmOpen) return;

    const modified = isDirty ?? (
      extraDirty || (form && !formHelpers.formValuesAreEqual(form.getFieldsValue(true), initialValues))
    );

    if (!modified) {
      onClose?.(...args);
      return;
    }

    isConfirmOpen = true;
    elementHelpers.showConfirmModal({
      translate: t,
      onOk: () => onClose?.(...args),
      afterClose: () => { isConfirmOpen = false; },
      title: t('exitConfirm.title'),
      content: t('exitConfirm.description'),
      zIndex: CONFIRM_Z_INDEX
    });
  }, [extraDirty, form, initialValues, isDirty, onClose, t]);
};
